import environment from "common/environments";
import Cookies from "js-cookie";

export const initAnalytics = () => {
  if (process.env.NODE_ENV !== "production") return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag("config", environment.dashboardGA4MeasurementId, { send_page_view: false });
};

export const identifyAnalyticsUser = (userId: string) => {
  if (process.env.NODE_ENV !== "production" || !window.gtag) return;

  window.gtag("config", environment.dashboardGA4MeasurementId, { user_id: userId, send_page_view: false });
};

export const sendAnalyticsEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (process.env.NODE_ENV !== "production" || !window.gtag) return;

  window.gtag("event", eventName, {
    ...params,
    send_to: environment.dashboardGA4MeasurementId,
  });
};

export const sendAdsConversionEvent = (tagId: string) => {
  if (process.env.NODE_ENV !== "production" || !window.gtag) return;

  window.gtag("event", "conversion", {
    send_to: tagId,
  });
};

interface GA4StreamCookieData {
  sessionId: string;
  sessionNumber: string;
  sessionEngaged: string;
  lastHitTimestamp: string;
  joinTimer: string;
  enhancedUserIdLoggedInState: string;
  enhancedUserId: string;
  joinId: string;
}

/**
 * Parses the GA4 stream cookie (_ga_XXXXXX) and extracts session data.
 * Supports both GS1 (old format) and GS2 (new format) cookie structures.
 *
 * GS1 format: GS1.1.<sessionId>.<sessionNumber>.<sessionEngaged>.<lastHitTimestamp>...
 * GS2 format: GS2.1.s<sessionId>$o<sessionNumber>$g<sessionEngaged>$t<lastHitTimestamp>...
 *
 * @see https://www.thyngster.com/google-analytics-4-cookie-format-change-from-gs1-to-gs2-explained
 */
const parseGoogleStreamCookie = (str: string): Partial<GA4StreamCookieData> => {
  const mapping: Record<string, keyof GA4StreamCookieData> = {
    s: "sessionId",
    o: "sessionNumber",
    g: "sessionEngaged",
    t: "lastHitTimestamp",
    j: "joinTimer",
    l: "enhancedUserIdLoggedInState",
    h: "enhancedUserId",
    d: "joinId",
  };

  const parts = str.split(".");
  const version = parts[0];
  const result: Partial<GA4StreamCookieData> = {};

  if (version === "GS1") {
    // GS1: Values are in fixed positions after the header
    const values = parts.slice(2);
    const keys: (keyof typeof mapping)[] = ["s", "o", "g", "t", "j", "l", "h", "d"];

    for (let i = 0; i < keys.length; i++) {
      const fieldName = mapping[keys[i]];
      result[fieldName] = values[i] || "";
    }
  } else {
    // GS2: Values have single-letter prefixes separated by $
    const segments = parts[2].replace(/%24/g, "$").split("$");

    for (let j = 0; j < segments.length; j++) {
      const prefix = segments[j][0];
      const value = decodeURIComponent(segments[j].slice(1));
      const fieldName = mapping[prefix];
      if (fieldName) {
        result[fieldName] = value;
      }
    }
  }

  return result;
};

export const getGA4Ids = (): { clientId: string | undefined; sessionId: string | undefined } => {
  let clientId: string | undefined;
  let sessionId: string | undefined;

  const gaCookie = Cookies.get("_ga");
  if (gaCookie) {
    const gaParts = gaCookie.split(".");
    if (gaParts.length >= 4) {
      clientId = [gaParts[2], gaParts[3]].join(".");
    }
  }

  const allCookies = document.cookie.split(";");
  const ga4Cookie = allCookies.find((cookie) => cookie.trim().startsWith("_ga_"));

  if (ga4Cookie) {
    const [, cookieValue] = ga4Cookie.split("=");
    if (cookieValue) {
      const parsedData = parseGoogleStreamCookie(cookieValue);
      sessionId = parsedData.sessionId;
    }
  }

  return { clientId, sessionId };
};

type AnalyticsCookies = {
  shk_utm_source?: string;
  shk_utm_medium?: string;
  shk_utm_campaign?: string;
  shk_utm_content?: string;
  shk_utm_term?: string;
  shk_referral_url?: string;
  shk_google_analytics_id?: string;
};

export const getAnalyticsDataFromCookies = (): AnalyticsCookies => {
  let googleAnalyticsId = "";
  const googleAnalyticsCookie = Cookies.get("_ga")?.split(".");
  if (googleAnalyticsCookie && googleAnalyticsCookie.length == 4)
    googleAnalyticsId = [googleAnalyticsCookie[2], googleAnalyticsCookie[3]].join(".");

  const analyticsData: AnalyticsCookies = {
    shk_utm_source: Cookies.get("shk_utm_source"),
    shk_utm_medium: Cookies.get("shk_utm_medium"),
    shk_utm_campaign: Cookies.get("shk_utm_campaign"),
    shk_utm_content: Cookies.get("shk_utm_content"),
    shk_utm_term: Cookies.get("shk_utm_term"),
    shk_referral_url: Cookies.get("shk_referral_url"),
    shk_google_analytics_id: googleAnalyticsId,
  };

  if (!analyticsData.shk_utm_source) delete analyticsData.shk_utm_source;
  if (!analyticsData.shk_utm_medium) delete analyticsData.shk_utm_medium;
  if (!analyticsData.shk_utm_campaign) delete analyticsData.shk_utm_campaign;
  if (!analyticsData.shk_utm_content) delete analyticsData.shk_utm_content;
  if (!analyticsData.shk_utm_term) delete analyticsData.shk_utm_term;
  if (!analyticsData.shk_referral_url) delete analyticsData.shk_referral_url;
  if (!analyticsData.shk_google_analytics_id) delete analyticsData.shk_google_analytics_id;

  return analyticsData;
};
